import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CloudOff, FileDown, Link2Off } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { CandidateOfferView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { errorMessage } from '../../../lib/api-client';
import { formatDateTime } from '../../../lib/format';
import { CandidateDocuments } from '../../hires/components/CandidateDocuments';
import { candidateOfferApi, candidateOfferKey, useCandidateOffer } from '../api/offers-api';
import styles from './CandidateOffer.module.css';

/** D13: a candidate reads Bangladesh time — the zone the company works in. */
const CANDIDATE_ZONE = 'Asia/Dhaka';

type Step = 'read' | 'confirm' | 'decline';

/**
 * The candidate's offer page from N-50's link (F13.06–F13.08) — no account (D16). Built at
 * 375px first (CLAUDE.md §5). They read the letter, download it as a PDF, and accept or decline;
 * accepting takes one more tap to confirm, because it can't be taken back here, and declining
 * asks why (§L20). The link answers once; afterwards it shows the answer.
 */
export function CandidateOfferPage({ token }: { readonly token: string }): ReactNode {
  const query = useCandidateOffer(token);
  if (token === '') return <Notice icon={Link2Off} title="This link doesn't work" body="Use the link in the latest email we sent you." />;
  if (query.isPending) {
    return (
      <Frame>
        <SkeletonLines count={8} />
      </Frame>
    );
  }
  if (query.error) {
    if (query.error.status === 404) return <Notice icon={Link2Off} title="This link doesn't work" body={query.error.message} />;
    return <Notice icon={CloudOff} title="We couldn't load your offer" body="Check your connection and try again." action={<Button variant="solid" onClick={() => void query.refetch()}>Try again</Button>} />;
  }
  return <Offer token={token} view={query.data} />;
}

function Offer({ token, view }: { readonly token: string; readonly view: CandidateOfferView }): ReactNode {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('read');
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [pending, setPending] = useState<'accept' | 'decline' | 'pdf' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function act(label: 'accept' | 'decline', call: () => Promise<CandidateOfferView>): Promise<void> {
    setPending(label);
    setProblem(null);
    try {
      queryClient.setQueryData(candidateOfferKey(token), await call());
      setStep('read');
    } catch (error) {
      setProblem(errorMessage(error));
      await queryClient.invalidateQueries({ queryKey: candidateOfferKey(token) });
    } finally {
      setPending(null);
    }
  }

  async function download(): Promise<void> {
    setPending('pdf');
    setProblem(null);
    try {
      await candidateOfferApi.downloadPdf(token, `${view.reference}-v${view.versionNumber}.pdf`);
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setPending(null);
    }
  }

  if (view.state === 'declined') {
    return <Notice icon={CheckCircle2} title={`Thank you, ${view.candidateFirstName}`} body={`We've let the ${view.companyName} team know you have declined the offer for ${view.jobTitle}. We wish you every success.`} />;
  }
  if (view.state === 'closed') {
    return <Notice icon={Link2Off} title="This offer is no longer open" body={view.closedReason ?? `The ${view.companyName} recruitment team will be in touch.`} />;
  }

  const isAccepted = view.state === 'accepted';
  return (
    <Frame>
      <p className={styles.quiet}>{view.companyName}</p>
      <h1 className={styles.title}>{isAccepted ? `Welcome aboard, ${view.candidateFirstName}` : `Your offer, ${view.candidateFirstName}`}</h1>
      <p className={styles.lead}>
        {isAccepted
          ? `You have accepted the offer for ${view.jobTitle}. The ${view.companyName} team will be in touch about your first day.`
          : `${view.jobTitle} at ${view.companyName}. Please accept or decline by ${view.validUntil ? formatDateTime(view.validUntil, { timeZone: CANDIDATE_ZONE }) : 'the date in the letter'}.`}
      </p>

      {isAccepted && <CandidateDocuments token={token} />}

      <article className={styles.letter} aria-label="Your offer letter">
        {view.paragraphs.map((paragraph, index) => (
          <p key={index} className={styles.paragraph}>
            {paragraph}
          </p>
        ))}
      </article>

      <Button variant="outline" size="lg" icon={FileDown} isPending={pending === 'pdf'} onClick={() => void download()}>
        Download the letter (PDF)
      </Button>

      {problem && <p className={styles.warning}>{problem}</p>}

      {!isAccepted && step === 'read' && (
        <div className={styles.choices}>
          <Button variant="solid" size="lg" block onClick={() => setStep('confirm')}>
            Accept the offer
          </Button>
          <Button variant="ghost" size="lg" block onClick={() => setStep('decline')}>
            Decline
          </Button>
        </div>
      )}

      {!isAccepted && step === 'confirm' && (
        <div className={styles.panel}>
          <p className={styles.panelText}>You are accepting the offer for {view.jobTitle} as set out in the letter above (reference {view.reference}, version {view.versionNumber}).</p>
          <Button variant="solid" size="lg" block isPending={pending === 'accept'} onClick={() => void act('accept', () => candidateOfferApi.accept(token))}>
            Yes, I accept
          </Button>
          <Button variant="ghost" size="lg" block onClick={() => setStep('read')}>
            Go back
          </Button>
        </div>
      )}

      {!isAccepted && step === 'decline' && (
        <div className={styles.panel}>
          <Field label="Could you tell us why?" isRequired hint="It helps us improve. Your answer goes to the recruitment team only.">
            {({ inputId }) => <Select id={inputId} label="Why you are declining" placeholder="Choose a reason" value={reasonCode} options={view.declineReasons.map((reason) => ({ value: reason.code, label: reason.label }))} onChange={setReasonCode} />}
          </Field>
          <Field label="Anything you'd like to add" isOptional>
            {({ inputId }) => <Textarea id={inputId} rows={3} maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} />}
          </Field>
          <Button
            variant="dangerSolid"
            size="lg"
            block
            disabled={reasonCode === null}
            isPending={pending === 'decline'}
            onClick={() => void act('decline', () => candidateOfferApi.decline(token, { reasonCode: reasonCode ?? '', note: note.trim() || null }))}
          >
            Decline the offer
          </Button>
          <Button variant="ghost" size="lg" block onClick={() => setStep('read')}>
            Go back
          </Button>
        </div>
      )}

      <p className={styles.quiet}>Questions about the offer? Reply to the email this link came in.</p>
    </Frame>
  );
}

function Frame({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <main className={styles.page}>
      <div className={styles.card}>{children}</div>
    </main>
  );
}

function Notice({ icon: Icon, title, body, action }: { readonly icon: typeof Link2Off; readonly title: string; readonly body: string; readonly action?: ReactNode }): ReactNode {
  return (
    <Frame>
      <Icon size={28} aria-hidden />
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.lead}>{body}</p>
      {action}
    </Frame>
  );
}
